/**
 * Fantasy Console 0 - Keyboard Shortcuts Modal
 */

import { useCallback, useEffect, useRef } from 'react'
import { SHORTCUT_SECTIONS } from '../../hooks/useKeyboardShortcuts'
import styles from './ShortcutsModal.module.css'

interface ShortcutsModalProps {
  isOpen: boolean
  onClose: () => void
}

export function ShortcutsModal({ isOpen, onClose }: ShortcutsModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      modalRef.current?.focus()
    }
  }, [isOpen])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose],
  )

  if (!isOpen) return null

  return (
    <div className='modal-overlay' onClick={onClose} onKeyDown={handleKeyDown}>
      <div
        ref={modalRef}
        className='modal-content wide'
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        <div className='modal-header'>
          <h2 className='modal-title'>Keyboard Shortcuts</h2>
        </div>

        <div className='modal-body'>
          <div className={styles.sectionsGrid}>
            {SHORTCUT_SECTIONS.map((section) => (
              <div key={section.title} className={styles.section}>
                <h3 className={styles.sectionTitle}>{section.title}</h3>
                <table className={styles.shortcutsTable}>
                  <tbody>
                    {section.shortcuts.map((shortcut, index) => (
                      <tr key={index}>
                        <td className={styles.keysCell}>
                          {shortcut.keys.map((key, keyIndex) => (
                            <span key={keyIndex}>
                              <kbd className={styles.key}>{key}</kbd>
                              {keyIndex < shortcut.keys.length - 1 && (
                                <span className={styles.plus}>+</span>
                              )}
                            </span>
                          ))}
                        </td>
                        <td>{shortcut.action}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>

        <div className='modal-footer'>
          <button type='button' className='btn btn-primary' onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
